// Geolocation helpers for BentSkin Go
import api from "./api";

// Capital / main-city centers per country (lat, lng)
export const COUNTRY_CENTERS = {
  CM: { lat: 3.848, lng: 11.502, name: "Yaoundé" },
  GA: { lat: 0.4162, lng: 9.4673, name: "Libreville" },
  TD: { lat: 12.1348, lng: 15.0557, name: "N'Djamena" },
  CG: { lat: -4.2634, lng: 15.2429, name: "Brazzaville" },
  CF: { lat: 4.3947, lng: 18.5582, name: "Bangui" },
  GQ: { lat: 3.7523, lng: 8.7742, name: "Malabo" },
};

// Villes desservies au Cameroun (départ rapide dans le formulaire de course)
export const CM_CITIES = [
  { name: "Yaoundé", lat: 3.848, lng: 11.502 },
  { name: "Douala", lat: 4.0511, lng: 9.7679 },
  { name: "Bafoussam", lat: 5.4781, lng: 10.4176 },
  { name: "Garoua", lat: 9.3017, lng: 13.3921 },
  { name: "Maroua", lat: 10.591, lng: 14.3153 },
  { name: "Ngaoundéré", lat: 7.3167, lng: 13.5833 },
  { name: "Bamenda", lat: 5.9597, lng: 10.1459 },
  { name: "Bertoua", lat: 4.5772, lng: 13.6846 },
];

// Villes desservies en Europe — Bruxelles (marché prioritaire) en tête.
export const EU_CITIES = [
  { name: "Bruxelles", lat: 50.8503, lng: 4.3517, cc: "BE" },
  { name: "Anvers", lat: 51.2194, lng: 4.4025, cc: "BE" },
  { name: "Liège", lat: 50.6326, lng: 5.5797, cc: "BE" },
  { name: "Gand", lat: 51.0543, lng: 3.7174, cc: "BE" },
  { name: "Charleroi", lat: 50.4108, lng: 4.4446, cc: "BE" },
  { name: "Paris", lat: 48.8566, lng: 2.3522, cc: "FR" },
  { name: "Amsterdam", lat: 52.3676, lng: 4.9041, cc: "NL" },
];

export function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Road distance ≈ straight-line * factor, min 1 km
export function roadDistanceKm(a, b) {
  const d = haversineKm(a, b) * 1.35;
  return Math.max(1, Math.round(d * 10) / 10);
}

export async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`,
      { headers: { "Accept-Language": "fr" } }
    );
    const j = await r.json();
    const a = j.address || {};
    const parts = [a.road || a.pedestrian || a.neighbourhood || a.suburb, a.city || a.town || a.village || a.county].filter(Boolean);
    return parts.join(", ") || j.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
}

const COUNTRY_ISO = { CM: "cm", GA: "ga", TD: "td", CG: "cg", CF: "cf", GQ: "gq" };

export async function forwardGeocode(query, countryCode) {
  if (!query || !query.trim()) return null;
  try {
    const cc = COUNTRY_ISO[countryCode] || "";
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=1${cc ? `&countrycodes=${cc}` : ""}`;
    const r = await fetch(url, { headers: { "Accept-Language": "fr" } });
    const j = await r.json();
    if (Array.isArray(j) && j.length) return { lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) };
  } catch { /* ignore */ }
  return null;
}

// ISO country code (uppercase, e.g. "FR", "CM") from GPS coords — via BACKEND proxy (avoids browser CORS on Nominatim).
export async function countryFromCoords(lat, lng) {
  try {
    const { data } = await api.get(`/geo/country?lat=${lat}&lng=${lng}`);
    return String(data?.country_code || "").toUpperCase();
  } catch {
    return "";
  }
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Géolocalisation non supportée"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

// Keep the screen awake so the GPS keeps emitting while on duty (web max — no screen-off).
export async function acquireWakeLock() {
  try { if ("wakeLock" in navigator) return await navigator.wakeLock.request("screen"); } catch { /* denied / unsupported */ }
  return null;
}


// Build a Google Maps directions deep-link: opens the native Google Maps app / web
// with a route driver -> passenger (waypoint) -> destination. No API key needed.
// navigate=true launches turn-by-turn navigation directly (driver mode).
export function googleMapsDirUrl(points, navigate = false) {
  const fmt = (p) => (p && p.lat != null && p.lng != null ? `${p.lat},${p.lng}` : null);
  const parts = (points || []).map(fmt).filter(Boolean);
  const destination = parts.pop();
  const origin = parts.shift();
  const waypoints = parts.join("|");
  let u = "https://www.google.com/maps/dir/?api=1";
  if (origin) u += `&origin=${origin}`;
  if (destination) u += `&destination=${destination}`;
  if (waypoints) u += `&waypoints=${encodeURIComponent(waypoints)}`;
  u += "&travelmode=driving";
  if (navigate) u += "&dir_action=navigate";
  return u;
}
